import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, AlertCircle, CheckCircle } from 'lucide-react';
import { ServiceExecutiveService } from '../../services/serviceExecutiveService';

const ServiceExecutiveDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');

  const { data: complaintsData, isLoading: complaintsLoading } = useQuery({
    queryKey: ['serviceExecutiveComplaints', user?.id],
    queryFn: () => ServiceExecutiveService.getComplaints(user?.id),
    enabled: !!user?.id,
  });

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['serviceExecutiveOrders', user?.id],
    queryFn: () => ServiceExecutiveService.getOrders(user?.id),
    enabled: !!user?.id,
  });

  const complaints = complaintsData?.data || [];
  const orders = ordersData?.data || [];

  const stats = {
    activeOrders: orders.filter(o => o.status !== 'completed' && o.status !== 'cancelled').length,
    complaints: complaints.length,
    completedToday: orders.filter(o => {
      if (!o.completed_at) return false;
      const today = new Date();
      const completed = new Date(o.completed_at);
      return completed.toDateString() === today.toDateString() && o.status === 'completed';
    }).length,
  };

  if (complaintsLoading || ordersLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">Service Executive Dashboard</h1>
          <p className="text-white/80">Order execution and service delivery coordination</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Service Executive Dashboard</h1>
        <p className="text-white/80">Order execution and service delivery coordination</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Active Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeOrders}</p>
            </div>
            <ClipboardList size={24} className="text-emerald-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Complaints</p>
              <p className="text-2xl font-bold text-gray-900">{stats.complaints}</p>
            </div>
            <AlertCircle size={24} className="text-red-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Completed Today</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedToday}</p>
            </div>
            <CheckCircle size={24} className="text-green-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button 
            onClick={() => navigate('/service_executive/complaints')}
            className="px-4 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
          >
            Manage Complaints
          </button>
          <button 
            onClick={() => navigate('/service_executive/orders')}
            className="px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
          >
            View Orders
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceExecutiveDashboard;

